import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { PenSquare, ImagePlay, X, ImagePlus, Video } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PostCard } from "@/components/PostCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const CATEGORIES = [
  { id: "", label: "All" },
  { id: "discussion", label: "Discussions" },
  { id: "confession", label: "Confessions" },
  { id: "meme", label: "Memes" },
  { id: "question", label: "Questions" },
  { id: "campus_tea", label: "Campus Tea" },
  { id: "poll", label: "Polls" },
];
const POSTABLE_CATEGORIES = CATEGORIES.filter((c) => c.id && c.id !== "poll");
let pollOptSeq = 0;
const newPollOpt = (text = "") => ({ key: `po-${++pollOptSeq}`, text });

export default function Campus() {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const [tab, setTab] = useState("latest");
  const [category, setCategory] = useState(location.pathname.endsWith("/confessions") ? "confession" : "");
  const [posts, setPosts] = useState(null);
  const [createOpen, setCreateOpen] = useState(params.get("create") === "1");
  const [form, setForm] = useState({ content: "", category: "discussion", is_anonymous: false });
  const [pollMode, setPollMode] = useState(false);
  const [pollOptions, setPollOptions] = useState(() => [newPollOpt(), newPollOpt()]);
  const [gif, setGif] = useState(null);
  const [gifOpen, setGifOpen] = useState(false);
  const [gifQuery, setGifQuery] = useState("");
  const [gifResults, setGifResults] = useState([]);
  const [image, setImage] = useState(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [showVideo, setShowVideo] = useState(false);
  const [busy, setBusy] = useState(false);

  const onImageFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) return toast.error("Image must be under 2MB");
    const reader = new FileReader();
    reader.onload = () => setImage(String(reader.result).split(",")[1]);
    reader.readAsDataURL(f);
  };

  const searchGifs = async (e) => {
    e?.preventDefault();
    if (!gifQuery.trim()) return;
    try {
      const { data } = await api.get("/giphy/search", { params: { q: gifQuery } });
      setGifResults(data);
    } catch (err) { toast.error(apiError(err)); }
  };

  const load = useCallback(() => {
    api.get("/posts", { params: { tab, category: category || undefined } })
      .then(({ data }) => setPosts(data)).catch(() => setPosts([]));
  }, [tab, category]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.content.trim()) return toast.error("Write something first");
    setBusy(true);
    try {
      const payload = { ...form, gif_url: gif?.url || null, image_base64: image, video_url: videoUrl.trim() || null };
      if (pollMode) {
        payload.category = "poll";
        payload.poll_options = pollOptions.map((o) => o.text).filter((t) => t.trim());
        if (payload.poll_options.length < 2) { setBusy(false); return toast.error("A poll needs at least 2 options"); }
      }
      await api.post("/posts", payload);
      toast.success(form.is_anonymous ? "Posted anonymously" : "Posted!");
      setCreateOpen(false);
      setForm({ content: "", category: "discussion", is_anonymous: false });
      setPollMode(false); setPollOptions([newPollOpt(), newPollOpt()]);
      setGif(null); setGifOpen(false); setGifQuery(""); setGifResults([]);
      setImage(null); setVideoUrl(""); setShowVideo(false);
      setParams({});
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="campus-page">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-black text-3xl">Campus</h1>
        <Button data-testid="create-post-btn" onClick={() => setCreateOpen(true)} className="rounded-full font-bold">
          <PenSquare className="w-4 h-4 mr-2" /> Post
        </Button>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {["latest", "trending"].map((t) => (
          <button key={t} data-testid={`feed-tab-${t}`} onClick={() => setTab(t)}
            className={`rounded-full px-4 py-1.5 text-sm font-bold shrink-0 transition-colors duration-200 ${tab === t ? "bg-foreground text-white" : "bg-black/5 hover:bg-black/10"}`}>
            {t === "latest" ? "Latest" : "Trending"}
          </button>
        ))}
        <span className="w-px bg-border shrink-0" />
        {CATEGORIES.map((c) => (
          <button key={c.id} data-testid={`category-chip-${c.id || "all"}`} onClick={() => setCategory(c.id)}
            className={`rounded-full px-4 py-1.5 text-sm font-bold shrink-0 transition-colors duration-200 ${category === c.id ? "bg-primary text-white" : "bg-black/5 hover:bg-black/10"}`}>
            {c.label}
          </button>
        ))}
      </div>

      {posts === null ? (
        <p className="text-muted-foreground animate-pulse text-center py-10">Loading feed…</p>
      ) : posts.length ? (
        <div className="space-y-4">{posts.map((p) => <PostCard key={p.id} post={p} onChange={load} />)}</div>
      ) : (
        <div className="t-card p-10 text-center" data-testid="empty-feed">
          <p className="font-display font-bold">It's quiet here…</p>
          <p className="text-sm text-muted-foreground mt-1">Be the first to post in {category ? CATEGORIES.find((c) => c.id === category)?.label : "the feed"}.</p>
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-3xl">
          <DialogHeader><DialogTitle>New post</DialogTitle></DialogHeader>
          <Textarea data-testid="post-content-input" rows={4} placeholder="What's happening on campus?"
            value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="rounded-2xl" />
          {!pollMode && (
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="post-category-select" className="rounded-2xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                {POSTABLE_CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {pollMode && (
            <div className="space-y-2">
              {pollOptions.map((o, i) => (
                <Input key={o.key} data-testid={`poll-input-${i}`} placeholder={`Option ${i + 1}`} value={o.text}
                  onChange={(e) => setPollOptions(pollOptions.map((x) => (x.key === o.key ? { ...x, text: e.target.value } : x)))} className="rounded-2xl" />
              ))}
              {pollOptions.length < 6 && (
                <button data-testid="add-poll-option-btn" onClick={() => setPollOptions([...pollOptions, newPollOpt()])} className="text-sm font-bold text-primary">+ Add option</button>
              )}
            </div>
          )}
          {gif && (
            <div className="relative w-fit">
              <img src={gif.url} alt="Selected GIF" className="rounded-2xl max-h-40" data-testid="selected-gif" />
              <button data-testid="remove-gif-btn" onClick={() => setGif(null)}
                className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-foreground text-white flex items-center justify-center">
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
          {gifOpen && !gif && (
            <div className="space-y-2">
              <form onSubmit={searchGifs} className="flex gap-2">
                <Input data-testid="gif-search-input" placeholder="Search GIFs…" value={gifQuery}
                  onChange={(e) => setGifQuery(e.target.value)} className="rounded-full" />
                <Button data-testid="gif-search-btn" type="submit" size="sm" className="rounded-full font-bold">Search</Button>
              </form>
              {gifResults.length > 0 && (
                <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                  {gifResults.map((g) => (
                    <button key={g.id} data-testid={`gif-result-${g.id}`} onClick={() => { setGif(g); setGifOpen(false); }}
                      className="rounded-xl overflow-hidden hover:opacity-80 transition-opacity duration-200">
                      <img src={g.preview} alt="GIF option" className="w-full h-20 object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {image && (
            <div className="relative w-fit">
              <img src={`data:image/jpeg;base64,${image}`} alt="Upload preview" className="rounded-2xl max-h-40" data-testid="selected-image" />
              <button data-testid="remove-image-btn" onClick={() => setImage(null)}
                className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-foreground text-white flex items-center justify-center">
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
          {showVideo && (
            <Input data-testid="video-url-input" placeholder="Paste video link (YouTube or .mp4)"
              value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} className="rounded-2xl" />
          )}
          <div className="flex items-center gap-4">
            <label data-testid="image-upload-btn" className="flex items-center gap-1 text-sm font-bold text-muted-foreground hover:text-foreground cursor-pointer transition-colors duration-200">
              <ImagePlus className="w-4 h-4" /> Photo
              <input type="file" accept="image/jpeg,image/png,image/webp" onChange={onImageFile} className="hidden" />
            </label>
            <button data-testid="video-toggle-btn" type="button" onClick={() => setShowVideo(!showVideo)}
              className={`flex items-center gap-1 text-sm font-bold transition-colors duration-200 ${showVideo || videoUrl ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}>
              <Video className="w-4 h-4" /> Video
            </button>
          </div>
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm font-semibold">
              <Switch data-testid="post-anonymous-switch" checked={form.is_anonymous} onCheckedChange={(v) => setForm({ ...form, is_anonymous: v })} />
              Post anonymously
            </label>
            <div className="flex items-center gap-3">
              <button data-testid="gif-toggle-btn" type="button" onClick={() => setGifOpen(!gifOpen)}
                className={`flex items-center gap-1 text-sm font-bold transition-colors duration-200 ${gifOpen || gif ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}>
                <ImagePlay className="w-4 h-4" /> GIF
              </button>
              <label className="flex items-center gap-2 text-sm font-semibold">
                <Switch data-testid="post-poll-switch" checked={pollMode} onCheckedChange={setPollMode} />
                Poll
              </label>
            </div>
          </div>
          {form.is_anonymous && (
            <p className="text-xs text-muted-foreground">Shown as “Anonymous — Verified Member”. Moderators can still act on abusive content.</p>
          )}
          <Button data-testid="post-submit-btn" disabled={busy} onClick={submit} className="rounded-full font-bold">
            {busy ? "Posting…" : "Post"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
